/**
 * THE ACCOUNT BLOCK at the foot of the super-admin sidebar (mockup "Account
 * option 2", chosen 2026-09-27). One compact row -- avatar, name, role, and a
 * dot in the environment's colour -- that opens a menu:
 *
 *   email · "Signed in · dev"
 *   My portal / Copy email / Copy user ID
 *   Sign out
 *
 * Sign-out moved into the menu on purpose: two deliberate clicks, never a
 * stray one on an icon next to your name. Custom menu, no native controls.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronUp, LogOut, UserCircle2, Mail, Fingerprint, Check, ShieldCheck } from 'lucide-react';
import { detectAppEnv, ENV_DOT, AppEnv } from './EnvBanner';

const ENV_WORD: Record<AppEnv, string> = {
  local: 'local (dev data)', dev: 'dev', staging: 'staging', prod: 'production',
};

/** The Cognito sub off the stored JWT, best effort. */
const userIdFromToken = (): string | null => {
  try {
    const t = localStorage.getItem('jwtToken');
    if (!t) return null;
    const claims = JSON.parse(atob((t.split('.')[1] || '').replace(/-/g, '+').replace(/_/g, '/')));
    return claims.sub || null;
  } catch {
    return null;
  }
};

export const initialsOf = (name: string | null | undefined, email: string | null | undefined): string => {
  const src = (name || '').trim();
  if (src) {
    const parts = src.split(/\s+/).filter(Boolean);
    return ((parts[0]?.[0] || '') + (parts[1]?.[0] || parts[0]?.[1] || '')).toUpperCase();
  }
  const local = (email || '?').split('@')[0].replace(/[^a-z0-9]/gi, '');
  return (local.slice(0, 2) || '?').toUpperCase();
};

export const AccountMenu: React.FC<{
  email: string | null | undefined;
  name?: string | null;
  role: string;
  onSignOut: () => void;
  onNavigate?: () => void;
}> = ({ email, name, role, onSignOut, onNavigate }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const wrap = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const env = detectAppEnv();
  const display = (name || '').trim() || (email || '').split('@')[0] || 'Signed in';

  // Close on an outside click or Escape -- only while open.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const copy = async (key: string, text: string | null | undefined) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(null), 1400);
    } catch { /* clipboard blocked */ }
  };

  const item = 'w-full min-h-[44px] px-3.5 flex items-center gap-2.5 text-[13px] text-stone-200 hover:bg-white/5 text-left';

  return (
    <div ref={wrap} className="relative flex-shrink-0 p-2.5 border-t border-dark-800 bg-black">
      {open && (
        <div
          role="menu"
          aria-label="Account"
          className="absolute left-2.5 right-2.5 bottom-[72px] z-10 rounded-2xl border border-zinc-700 bg-zinc-900 shadow-[0_18px_40px_rgba(0,0,0,0.6)] overflow-hidden"
        >
          <div className="px-3.5 py-3 border-b border-zinc-800">
            <div className="text-[13px] font-semibold text-white truncate">{email}</div>
            <div className="mt-0.5 text-[11px] text-stone-400 flex items-center gap-1.5">
              <span className="inline-block w-[7px] h-[7px] rounded-full" style={{ background: ENV_DOT[env] }} />
              Signed in · {ENV_WORD[env]}
            </div>
          </div>
          <button type="button" role="menuitem" className={item}
            onClick={() => { setOpen(false); onNavigate?.(); navigate('/staff-portal'); }}>
            <UserCircle2 size={16} className="text-stone-400" /> My portal
          </button>
          <button type="button" role="menuitem" className={item}
            onClick={() => { setOpen(false); onNavigate?.(); navigate('/my-security'); }}>
            <ShieldCheck size={16} className="text-stone-400" /> My security
          </button>
          <button type="button" role="menuitem" className={item} onClick={() => copy('email', email)}>
            {copied === 'email' ? <Check size={16} className="text-emerald-400" /> : <Mail size={16} className="text-stone-400" />}
            {copied === 'email' ? 'Copied' : 'Copy email'}
          </button>
          <button type="button" role="menuitem" className={item} onClick={() => copy('id', userIdFromToken())}>
            {copied === 'id' ? <Check size={16} className="text-emerald-400" /> : <Fingerprint size={16} className="text-stone-400" />}
            {copied === 'id' ? 'Copied' : 'Copy user ID'}
            <span className="ml-auto text-[11px] text-stone-500">for support</span>
          </button>
          <div className="h-px bg-zinc-800" />
          <button type="button" role="menuitem" className={`${item} font-semibold`} style={{ color: '#FF8FA3' }}
            onClick={() => { setOpen(false); onSignOut(); }}>
            <LogOut size={16} /> Sign out
          </button>
        </div>
      )}
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={`w-full min-h-[56px] rounded-xl border px-2.5 py-2 flex items-center gap-2.5 text-left transition-colors ${
          open ? 'bg-zinc-900 border-zinc-700' : 'border-dark-800 hover:bg-zinc-900/60'
        }`}
      >
        <span className="relative w-9 h-9 rounded-full flex items-center justify-center text-[13px] font-bold text-white flex-shrink-0" style={{ background: '#DC143C' }}>
          {initialsOf(name, email)}
          <span
            className="absolute -right-px -bottom-px w-[11px] h-[11px] rounded-full"
            style={{ background: ENV_DOT[env], boxShadow: '0 0 0 2px #000' }}
            aria-hidden="true"
          />
        </span>
        <span className="min-w-0 flex-1 flex flex-col">
          <span className="text-[13px] font-semibold text-white truncate">{display}</span>
          <span className="text-[11.5px] text-stone-400 truncate">{role}</span>
        </span>
        <ChevronUp size={16} className={`text-stone-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
    </div>
  );
};

export default AccountMenu;
